import React, { useState } from 'react';
import { X, Check, Copy, HardDrive } from 'lucide-react';
import { createGateway, CreatedGatewayRow, Owner } from '../services/adminService';

interface Props {
  owners: Owner[];
  onClose: () => void;
  onCreated: () => void;
}

// Two steps on purpose. Creating the rows is only half the job — the board is
// useless until its identity is written to NVS, so the second step hands over
// the exact provision.sh command rather than leaving the admin to assemble it
// from two keys and a port name.
const AddGatewayModal: React.FC<Props> = ({ owners, onClose, onCreated }) => {
  const [deviceId, setDeviceId] = useState('');
  const [name, setName] = useState('');
  const [userId, setUserId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedGatewayRow[] | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);

    const result = await createGateway(deviceId.trim(), name.trim(), userId || null);
    setBusy(false);

    if ('error' in result) {
      setError(result.error);
      return;
    }
    setCreated(result.rows);
    onCreated();
  };

  const readingsRow = created?.find((r) => !r.deviceId.endsWith('-hub'));

  const command = readingsRow
    ? `cd firmware/M3TRmon && tools/provision.sh ${readingsRow.deviceId}`
    : '';

  const copy = () => {
    navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-cyan-400" />
            <h2 className="font-semibold text-slate-100">
              {created ? 'Gateway created' : 'Add gateway'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!created ? (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Device ID</label>
              <input
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="m3trmon-002"
                required
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:border-cyan-500 outline-none"
              />
              <p className="text-xs text-slate-500 mt-1">
                Lowercase, digits and hyphens. The telemetry identity
                (<span className="font-mono">-hub</span>) is created automatically.
              </p>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Hub 002"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:border-cyan-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Customer (optional)</label>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:border-cyan-500 outline-none"
              >
                <option value="">Unassigned</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            {error && (
              <p className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-slate-200">
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !deviceId.trim()}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg font-medium transition-colors"
              >
                {busy ? 'Creating…' : 'Create'}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-emerald-400">
              <Check className="w-5 h-5" />
              <span className="text-sm">Both rows created. The board claims them on first contact.</span>
            </div>

            <div>
              <p className="text-sm text-slate-400 mb-2">
                Connect the board by USB and run:
              </p>
              <pre className="bg-slate-950 border border-slate-700 rounded-lg p-3 text-xs text-slate-300 overflow-x-auto whitespace-pre">
{command}
              </pre>
              <button
                onClick={copy}
                className="mt-2 flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Copied' : 'Copy command'}
              </button>
              <p className="text-xs text-slate-500 mt-2">
                Run it with the board connected by USB (external watchdog unplugged).
                The script reads the keys itself, so there is nothing to copy.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddGatewayModal;
